import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import SubdistrictSearch from '../components/SubdistrictSearch';
import DateOfBirthPicker, { isAtLeast18 } from '../components/DateOfBirthPicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ImagePlus, X, ShieldCheck, ShieldX, Lock, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { compressProfilePhoto } from '../utils/imageCompression';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = ['Skin care', 'Make up', 'Hair', 'Body'];
const SKIN_TYPES = ['Dry', 'Normal', 'Oily', 'Combination'];
const SKIN_CONCERNS = ['Acne', 'Sensitive', 'Fine lines', 'Big pores', 'Acne scars', 'Hyperpigmentation', 'Freckles', 'Lack of elasticity'];
const FOLLOWER_RANGES = ['1k-10k', '10k-50k', '50k-100k', '100k-1M', '>1M'];
const SOCIAL_PLATFORMS = ['TikTok', 'Instagram', 'Facebook'];
const COUNTRIES = ['Indonesia'];

export default function CompleteProfilePage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [influencerVerified, setInfluencerVerified] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [rejectionReason, setRejectionReason] = useState(null);
  const [profileExists, setProfileExists] = useState(false);
  const [form, setForm] = useState({
    name: '', dob: '', photo: '', city: '', country: 'Indonesia',
    subdistrict_id: null, subdistrict_label: '',
    interests: [], skin_type: '', skin_concerns: [],
    beauty_story: '',
    is_influencer: false, follower_range: '',
    social_platforms: [], social_handle: '',
  });

  useEffect(() => {
    if (user?.name) setForm(f => ({ ...f, name: user.name }));
    if (user?.picture) setForm(f => ({ ...f, photo: user.picture }));
    axios.get(`${API}/profiles/me`)
      .then(r => {
        if (r.data?.profile_completed) {
          setForm(f => ({ ...f, ...r.data }));
          setProfileExists(true);
          if (r.data?.influencer_verified) {
            setInfluencerVerified(true);
          }
          if (r.data?.influencer_verification_status) {
            setVerificationStatus(r.data.influencer_verification_status);
          }
          if (r.data?.rejection_reason) {
            setRejectionReason(r.data.rejection_reason);
          }
        }
      }).catch(() => {});
  }, [user]);

  // Back-button handler: treat Back on incomplete-profile as "cancel sign up" —
  // log the user out and send them to the homepage (no auto Google OAuth restart).
  //
  // CRITICAL: The handler MUST be synchronous. If we await the server-side logout
  // before calling `location.replace('/')`, a second quick Back press can pop past
  // our trap state and the browser leaves our app (landing on a cached Google OAuth
  // or emergent-auth URL from earlier in the history chain).
  const guardInstalled = useRef(false);
  useLayoutEffect(() => {
    // Only install for users whose profile is NOT completed. Known-completed
    // users get default browser back behaviour (this matches legacy behaviour).
    if (user?.profile_completed) return;
    if (guardInstalled.current) return;
    guardInstalled.current = true;

    // Push a same-URL dummy state so the first Back click triggers our popstate
    // handler without the browser actually leaving the page first.
    window.history.pushState({ completeProfileGuard: true }, '', window.location.pathname);

    const handlePopState = () => {
      // SYNCHRONOUS cleanup only — no awaits. Fire-and-forget server logout so
      // the browser can redirect instantly before any subsequent Back click.
      try {
        localStorage.removeItem('session_token');
        axios.post(`${API}/auth/logout`, {}).catch(() => { /* ignore */ });
      } catch { /* ignore */ }
      window.location.replace('/');
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      guardInstalled.current = false;
    };
  }, [user?.profile_completed]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const compressed = await compressProfilePhoto(file);
      const formData = new FormData();
      formData.append('file', compressed);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({ ...f, photo: `${process.env.REACT_APP_BACKEND_URL}${res.data.url}` }));
    } catch (err) {
      toast.error(err.message || 'Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  const toggleArrayItem = (field, item) => {
    setForm(f => ({
      ...f,
      [field]: f[field].includes(item)
        ? f[field].filter(i => i !== item)
        : [...f[field], item]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.photo) {
      toast.error('Profile picture is required');
      return;
    }
    if (!form.name) {
      toast.error('Name is required');
      return;
    }
    if (!form.dob) {
      toast.error('Date of birth is required');
      return;
    }
    if (!isAtLeast18(form.dob)) {
      toast.error('You must be at least 18 years old to use the platform');
      return;
    }
    if (!form.subdistrict_id) {
      toast.error('Subdistrict (Kecamatan) is required');
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API}/profiles/me`, form);
      await refreshUser();
      if (form.is_influencer && !influencerVerified) {
        toast.success('Influencer verification is pending. Please check your profile for your secret code. We\'ll send a verification message to your social account\'s DMs within 24 hours.', { duration: 7000 });
      } else {
        toast.success('Profile saved!');
      }
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] mb-2" data-testid="complete-profile-title">Complete Your Profile</h1>
          <p className="font-body text-[#78716C]">Tell us about yourself and your skin</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-cream-100/50">
          {/* Photo */}
          <div className="flex flex-col items-center gap-2" data-testid="profile-photo-section">
            <div className="relative">
              {form.photo ? (
                <div className="relative">
                  <img src={form.photo} alt="" className="w-28 h-28 rounded-full object-cover border-4 border-blush-200 shadow-md" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, photo: '' }))}
                    className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-sm border border-cream-100 hover:bg-red-50 transition-colors">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <label className="w-28 h-28 rounded-full border-[3px] border-dashed border-[#F4C2C2] bg-[#FFF5F5] flex flex-col items-center justify-center cursor-pointer hover:border-[#E8A0A0] hover:bg-[#FFEDED] transition-all shadow-sm" data-testid="profile-photo-placeholder">
                  {photoUploading ? (
                    <div className="w-6 h-6 border-2 border-blush-200 border-t-blush-400 rounded-full animate-spin" />
                  ) : (
                    <>
                      <ImagePlus size={24} className="text-[#D4A0A0] mb-1" />
                      <span className="text-[10px] font-body font-medium text-[#D4A0A0]">Add Photo *</span>
                    </>
                  )}
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="profile-photo-input" />
                </label>
              )}
            </div>
          </div>

          {/* Name & DOB */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Name *</label>
              <input
                type="text" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                data-testid="input-profile-name"
              />
            </div>
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Date of Birth *</label>
              <DateOfBirthPicker
                value={form.dob}
                onChange={dob => setForm(f => ({ ...f, dob }))}
              />
            </div>
          </div>

          {/* Subdistrict & Country */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Subdistrict (Kecamatan) *</label>
              <SubdistrictSearch
                value={form.subdistrict_id}
                label={form.subdistrict_label}
                onChange={({ id, label, city }) => setForm(f => ({ ...f, subdistrict_id: id, subdistrict_label: label, city: city }))}
              />
            </div>
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Country</label>
              <input
                type="text" value="Indonesia" disabled
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#78716C] cursor-not-allowed"
                data-testid="input-profile-country"
              />
            </div>
          </div>

          {/* Beauty Story */}
          <div data-testid="beauty-story-section">
            <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">Your Beauty Story <span className="text-[#A89F91] font-normal">(optional)</span></label>
            <p className="text-xs font-body text-[#A89F91] mb-2">Share your beauty journey in a few words — serious or witty, it's your call!</p>
            <div className="relative">
              <textarea
                value={form.beauty_story}
                onChange={e => {
                  if (e.target.value.length <= 200) {
                    setForm(f => ({ ...f, beauty_story: e.target.value }));
                  }
                }}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent resize-none"
                rows={3}
                placeholder="e.g., Sunscreen is my love language."
                data-testid="input-beauty-story"
              />
              <span className={`absolute bottom-3 right-3 text-xs font-body ${
                form.beauty_story.length >= 200 ? 'text-red-400' : 'text-[#A89F91]'
              }`} data-testid="beauty-story-counter">
                {form.beauty_story.length}/200
              </span>
            </div>
          </div>

          {/* Interests */}
          <div data-testid="interests-section">
            <label className="block text-sm font-body font-medium text-[#78716C] mb-2">Interests</label>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map(interest => (
                <button
                  key={interest} type="button"
                  onClick={() => toggleArrayItem('interests', interest)}
                  className={`rounded-full px-4 py-2 text-sm font-body font-medium transition-all ${
                    form.interests.includes(interest)
                      ? 'bg-[#F4C2C2] text-[#2C2A29]'
                      : 'bg-cream-50 text-[#78716C] border border-cream-100 hover:border-[#F4C2C2]'
                  }`}
                  data-testid={`interest-${interest.toLowerCase().replace(/ /g, '-')}`}
                >
                  {interest}
                </button>
              ))}
            </div>
          </div>

          {/* Skin Type */}
          <div data-testid="skin-type-section">
            <label className="block text-sm font-body font-medium text-[#78716C] mb-2">Skin Type</label>
            <Select value={form.skin_type} onValueChange={v => setForm(f => ({ ...f, skin_type: v }))}>
              <SelectTrigger className="bg-cream-50 border-cream-100 rounded-xl h-12" data-testid="select-skin-type">
                <SelectValue placeholder="Select your skin type" />
              </SelectTrigger>
              <SelectContent>
                {SKIN_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Skin Concerns */}
          <div data-testid="skin-concerns-section">
            <label className="block text-sm font-body font-medium text-[#78716C] mb-2">Skin Concerns</label>
            <div className="grid grid-cols-2 gap-2">
              {SKIN_CONCERNS.map(concern => (
                <label key={concern} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-cream-50 border border-cream-100 hover:border-[#F4C2C2] transition-all cursor-pointer" data-testid={`concern-${concern.toLowerCase().replace(/ /g, '-')}`}>
                  <Checkbox
                    checked={form.skin_concerns.includes(concern)}
                    onCheckedChange={(checked) => {
                      setForm(f => ({
                        ...f,
                        skin_concerns: checked
                          ? [...f.skin_concerns, concern]
                          : f.skin_concerns.filter(c => c !== concern)
                      }));
                    }}
                    className="data-[state=checked]:bg-[#6B1F2B] data-[state=checked]:border-[#6B1F2B]"
                  />
                  <span className="text-sm font-body text-[#2C2A29]">{concern}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Influencer Toggle */}
          <div className="flex items-center gap-3 py-2" data-testid="influencer-toggle">
            <Checkbox
              checked={form.is_influencer}
              onCheckedChange={v => {
                if (!profileExists && !influencerVerified) {
                  setForm(f => ({ ...f, is_influencer: v }));
                }
              }}
              disabled={profileExists || influencerVerified}
              className="border-cream-200 data-[state=checked]:bg-[#F4C2C2] data-[state=checked]:border-[#F4C2C2]"
              data-testid="checkbox-influencer"
            />
            <label className={`font-body text-sm text-[#2C2A29] font-medium ${profileExists || influencerVerified ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
              onClick={() => { if (!profileExists && !influencerVerified) setForm(f => ({ ...f, is_influencer: !f.is_influencer })); }}>
              I'm a beauty influencer
            </label>
            {profileExists && !influencerVerified && (
              <span className="flex items-center gap-1 text-xs font-body text-[#78716C] bg-cream-50 px-2 py-0.5 rounded-full">
                <Lock size={12} /> Locked
              </span>
            )}
            {influencerVerified && (
              <span className="flex items-center gap-1 text-xs font-body text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                <ShieldCheck size={12} /> Verified
              </span>
            )}
          </div>

          {/* Influencer Fields */}
          {form.is_influencer && (
            <div className={`space-y-4 rounded-2xl p-5 ${influencerVerified ? 'bg-green-50/50 border border-green-100' : 'bg-[#FFF5F5]'}`} data-testid="influencer-fields">
              {!influencerVerified && !profileExists && (
                <p className="text-xs font-body text-[#78716C] leading-relaxed">
                  Pick where you're most active. Make sure your details match. Create a profile first. Then tap Verify from your profile page.
                </p>
              )}
              {profileExists && !influencerVerified && (
                <div className="flex items-center gap-2 bg-cream-50 rounded-xl p-3">
                  <Lock size={14} className="text-[#78716C]" />
                  <p className="text-xs font-body text-[#78716C]">
                    Influencer details are locked after profile creation.
                  </p>
                </div>
              )}

              {/* Step 1: Pick your main platform */}
              <div>
                <label className="block text-sm font-body font-medium text-[#78716C] mb-2">Your Main Platform *</label>
                <div className="flex flex-wrap gap-2">
                  {SOCIAL_PLATFORMS.map(platform => (
                    <button
                      key={platform} type="button"
                      disabled={profileExists || influencerVerified}
                      onClick={() => setForm(f => ({ ...f, social_platforms: [platform] }))}
                      className={`rounded-full px-4 py-2 text-sm font-body font-medium transition-all ${
                        form.social_platforms.includes(platform)
                          ? 'bg-[#F4C2C2] text-[#2C2A29]'
                          : influencerVerified
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-[#78716C] border border-cream-100 hover:border-[#F4C2C2]'
                      } ${(profileExists || influencerVerified) && !form.social_platforms.includes(platform) ? 'hidden' : ''}`}
                      data-testid={`platform-${platform.toLowerCase()}`}
                    >
                      {platform}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 2: Username for that platform */}
              {form.social_platforms.length > 0 && (
                <div>
                  <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">
                    {form.social_platforms[0]} Username / Handle *
                  </label>
                  <input
                    type="text" value={form.social_handle}
                    disabled={profileExists || influencerVerified}
                    onChange={e => setForm(f => ({ ...f, social_handle: e.target.value }))}
                    className={`w-full border rounded-xl px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent ${
                      (profileExists || influencerVerified)
                        ? 'bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-white border-cream-100 text-[#2C2A29]'
                    }`}
                    placeholder={`@your${form.social_platforms[0].toLowerCase()}handle`}
                    data-testid="input-social-handle"
                  />
                </div>
              )}

              {/* Step 3: Follower range */}
              {form.social_platforms.length > 0 && (
                <div>
                  <label className="block text-sm font-body font-medium text-[#78716C] mb-1.5">
                    Followers on {form.social_platforms[0]} *
                  </label>
                  <Select
                    value={form.follower_range}
                    onValueChange={v => setForm(f => ({ ...f, follower_range: v }))}
                    disabled={profileExists || influencerVerified}
                  >
                    <SelectTrigger className={`rounded-xl h-12 ${
                      (profileExists || influencerVerified) ? 'bg-gray-50 border-gray-200 text-gray-500' : 'bg-white border-cream-100'
                    }`} data-testid="select-follower-range">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      {FOLLOWER_RANGES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Locked state for already verified */}
              {influencerVerified && verificationStatus === 'approved' && (
                <div className="flex items-center gap-2 bg-green-100 rounded-xl p-3" data-testid="influencer-approved-notice">
                  <ShieldCheck size={14} className="text-green-600 flex-shrink-0" />
                  <p className="text-xs font-body text-green-700">
                    We've verified your beauty creator profile. Your details are locked.
                  </p>
                </div>
              )}
              {profileExists && verificationStatus === 'pending_review' && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl p-3" data-testid="influencer-pending-notice">
                  <Clock size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs font-body text-amber-700">
                    Your verification is pending. Check your profile page for your secret code. We'll send a verification DM to your linked social account within 24 hours. You can still buy and list products on Stashmart.
                  </p>
                </div>
              )}
              {profileExists && verificationStatus === 'rejected' && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="influencer-rejected-notice">
                  <ShieldX size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs font-body text-red-600">
                    {rejectionReason === 'content_not_relevant'
                      ? "Verification rejected: Your content isn't focused enough on beauty."
                      : rejectionReason === 'wrong_expired_code'
                      ? "Verification rejected: Secret code is not valid."
                      : rejectionReason === 'account_private'
                      ? "Verification rejected: account is private."
                      : rejectionReason?.startsWith('other:')
                      ? `Verification rejected: ${rejectionReason.slice(6)}.`
                      : "Verification rejected."
                    } You can request verification again from your profile page.
                  </p>
                </div>
              )}
            </div>
          )}

          <button
            type="submit" disabled={loading}
            className="w-full bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-3.5 font-body font-semibold transition-all hover:bg-[#ECAEAE] hover:shadow-md disabled:opacity-50"
            data-testid="save-profile-btn"
          >
            {loading ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </main>
    </div>
  );
}
